import { z } from 'zod';

/**
 * Phase 7.1.5c — gateway audit-event taxonomy (Phase 7 unified shape).
 *
 * Stability contract: this discriminated union is consumed by 7.5's
 * connector admin console (apps/web/src/admin/connector-admin-console.tsx)
 * and the `skillspec://workspace/{id}/audit-log` MCP resource. It is also
 * consumed by `apps/api/src/audit-ingest/audit-ingest.service.ts` for
 * runtime validation on the wire.
 *
 * Adding a sibling event = non-breaking. Renaming or repurposing an event
 * = breaking; requires a `scope_version` bump per phase 7.4 §4.4 and a
 * coordinated 7.5 update.
 *
 * Canonical envelope (`BaseEvent`):
 *   - `ts` ISO timestamp.
 *   - `workspaceId` nullable (null for platform-level events such as
 *     `OAUTH_CLIENT_SUSPENDED` global kill-switch).
 *   - `actorUserId` xor `actorClientId` xor null (system).
 *   - `outcome` triple shared with the existing `AuditOutcome`.
 *   - `reason` required when outcome != 'success'.
 */

const BaseEvent = z.object({
  ts: z.string().datetime(),
  workspaceId: z.number().int().positive().nullable(),
  actorUserId: z.number().int().positive().nullable(),
  actorClientId: z.string().nullable(),
  outcome: z.enum(['success', 'denied', 'error']),
  reason: z.string().optional(),
});

// --- DCR / OAuthClient lifecycle ---------------------------------------

export const OAuthClientRegistered = BaseEvent.extend({
  type: z.literal('OAUTH_CLIENT_REGISTERED'),
  payload: z.object({
    clientId: z.string(),
    clientName: z.string(),
    redirectUriCount: z.number().int().nonnegative(),
    softwareStatementVerified: z.boolean(),
    initialStatus: z.enum(['pending', 'approved']),
    contactEmail: z.string().nullable(),
  }),
});

export const OAuthClientApproved = BaseEvent.extend({
  type: z.literal('OAUTH_CLIENT_APPROVED'),
  payload: z.object({
    clientId: z.string(),
    approvedByUserId: z.number().int().positive(),
    workspaceId: z.number().int().positive(),
    /** READ_ONLY | OPERATIONAL | FULL — UPPER_SNAKE per Prisma enum. */
    exposureProfile: z.enum(['READ_ONLY', 'OPERATIONAL', 'FULL']),
  }),
});

export const OAuthClientRejected = BaseEvent.extend({
  type: z.literal('OAUTH_CLIENT_REJECTED'),
  payload: z.object({
    clientId: z.string(),
    rejectedByUserId: z.number().int().positive(),
    workspaceId: z.number().int().positive(),
    rejectionReason: z.string().min(1).max(500),
  }),
});

export const OAuthClientSuspended = BaseEvent.extend({
  type: z.literal('OAUTH_CLIENT_SUSPENDED'),
  payload: z.object({
    clientId: z.string(),
    suspendedByUserId: z.number().int().positive(),
    suspensionReason: z.string().min(1).max(500),
    cascadedGrantCount: z.number().int().nonnegative(),
    /** v1 known gap — true when the suspend is global (no workspaceId). */
    platformWide: z.boolean(),
  }),
});

export const OAuthClientAutoApproved = BaseEvent.extend({
  type: z.literal('OAUTH_CLIENT_AUTO_APPROVED'),
  payload: z.object({
    clientId: z.string(),
    workspaceId: z.number().int().positive(),
    autoApprovedSource: z.enum(['REGISTRY_TRUST', 'SOFTWARE_STATEMENT']),
    exposureProfile: z.enum(['READ_ONLY', 'OPERATIONAL', 'FULL']),
    triggeringUserId: z.number().int().positive().nullable(),
  }),
});

// --- Grant lifecycle ---------------------------------------------------

export const OAuthGrantIssued = BaseEvent.extend({
  type: z.literal('OAUTH_GRANT_ISSUED'),
  payload: z.object({
    grantId: z.string(),
    clientId: z.string(),
    userId: z.number().int().positive(),
    workspaceId: z.number().int().positive(),
    scopes: z.array(z.string()).min(1),
    scopeVersion: z.number().int().positive(),
    profile: z.enum(['read-only', 'operational', 'full']),
    surface: z.enum(['cowork', 'chat', 'desktop', 'mobile']).optional(),
    /** True when issuance was short-circuited via per-workspace trust gate. */
    viaWorkspaceTrust: z.boolean(),
  }),
});

export const OAuthGrantRefreshed = BaseEvent.extend({
  type: z.literal('OAUTH_GRANT_REFRESHED'),
  payload: z.object({
    grantId: z.string(),
    clientId: z.string(),
    userId: z.number().int().positive(),
    workspaceId: z.number().int().positive(),
    scopes: z.array(z.string()),
    /** SHA-256 b64url of the new refresh-token jti. NEVER raw. */
    newRefreshTokenJtiHash: z.string(),
  }),
});

export const OAuthGrantRevoked = BaseEvent.extend({
  type: z.literal('OAUTH_GRANT_REVOKED'),
  payload: z.object({
    grantId: z.string(),
    clientId: z.string(),
    userId: z.number().int().positive(),
    workspaceId: z.number().int().positive(),
    /** Pinned vocabulary from 7.1.5b §8. */
    revocationReason: z.enum([
      'refresh_token_replay',
      'idp_user_disabled',
      'admin_revoke',
      'user_revoke',
      'client_suspended',
      'workspace_membership_removed',
      'org_grant_cascade',
      'workspace_deleted',
    ]),
  }),
});

export const OAuthScopeDenied = BaseEvent.extend({
  type: z.literal('OAUTH_SCOPE_DENIED'),
  payload: z.object({
    capabilityId: z.string(),
    requestedScope: z.string(),
    grantedScopes: z.array(z.string()),
    tokenScopeVersion: z.number().int(),
    minSupportedScopeVersion: z.number().int(),
    reasonCode: z.enum([
      'missing_scope',
      'stale_scope_version',
      'profile_filtered',
      'grant_revoked',
      'client_suspended',
      'role_downgrade',
    ]),
  }),
});

export const OAuthRefreshReplayDetected = BaseEvent.extend({
  type: z.literal('OAUTH_REFRESH_REPLAY_DETECTED'),
  payload: z.object({
    clientId: z.string(),
    grantId: z.string(),
    /** Whether chain-revoke fired in the same txn (always true unless DB fail). */
    chainRevoked: z.boolean(),
  }),
});

// --- Rate limit --------------------------------------------------------

export const RateLimitBreached = BaseEvent.extend({
  type: z.literal('RATE_LIMIT_BREACHED'),
  payload: z.object({
    bucket: z.enum([
      'per_workspace_client',
      'per_client_global',
      'dcr_per_ip',
      'dcr_per_cidr24',
      'dcr_per_client_name',
      'dcr_per_software_statement_issuer',
      'remote_beta_authorize_ip',
      'remote_beta_authorize_client_ip',
      'remote_beta_callback_ip',
      'remote_beta_callback_signed_flow',
      'remote_beta_exchange_ip',
      'remote_beta_exchange_client',
      'remote_beta_exchange_code_digest',
      'remote_beta_refresh_ip',
      'remote_beta_refresh_client',
      'remote_beta_refresh_grant_digest',
      'remote_beta_mcp_preauth_ip',
      'remote_beta_mcp_connection_user',
      'remote_beta_mcp_client',
    ]),
    workspaceId: z.number().int().positive().nullable(),
    clientId: z.string().nullable(),
    rps: z.number().int().positive(),
    burst: z.number().int().positive(),
    retryAfter: z.number().int().nonnegative(),
  }),
});

// --- JWKS rotation -----------------------------------------------------

export const JwksKeyRotated = BaseEvent.extend({
  type: z.literal('JWKS_KEY_ROTATED'),
  payload: z.object({
    kidOld: z.string().nullable(),
    kidNew: z.string(),
    retentionSeconds: z.number().int().nonnegative(),
    mode: z.enum(['scheduled', 'emergency', 'manual_rollback']),
  }),
});

export const JwksKeyRetired = BaseEvent.extend({
  type: z.literal('JWKS_KEY_RETIRED'),
  payload: z.object({
    kid: z.string(),
    retiredAt: z.string().datetime(),
  }),
});

export const JwksKeyExpired = BaseEvent.extend({
  type: z.literal('JWKS_KEY_EXPIRED'),
  payload: z.object({
    kid: z.string(),
    expiredAt: z.string().datetime(),
  }),
});

export const KeyCompromiseRotation = BaseEvent.extend({
  type: z.literal('KEY_COMPROMISE_ROTATION'),
  payload: z.object({
    retiredKid: z.string(),
    reason: z.string().min(1).max(500),
    actorUserId: z.number().int().positive().nullable(),
  }),
});

// --- Workspace registry / branding -------------------------------------

export const WorkspaceRegistryProfileChanged = BaseEvent.extend({
  type: z.literal('WORKSPACE_REGISTRY_PROFILE_CHANGED'),
  payload: z.object({
    workspaceId: z.number().int().positive(),
    previousProfile: z.enum(['READ_ONLY', 'OPERATIONAL', 'FULL']).nullable(),
    newProfile: z.enum(['READ_ONLY', 'OPERATIONAL', 'FULL']),
    actorUserId: z.number().int().positive(),
  }),
});

export const WorkspaceBrandingUpdated = BaseEvent.extend({
  type: z.literal('WORKSPACE_BRANDING_UPDATED'),
  payload: z.object({
    workspaceId: z.number().int().positive(),
    actorUserId: z.number().int().positive(),
    /** Map of fields that changed in this PATCH; values are not logged. */
    changedFields: z.array(
      z.enum([
        'customConsentLogoUrl',
        'customConsentDisplayName',
        'customConsentTouUrl',
        'customConsentDpaUrl',
      ])
    ),
  }),
});

// --- Discriminated union -----------------------------------------------

export const GatewayAuditEvent = z.discriminatedUnion('type', [
  OAuthClientRegistered, // 1
  OAuthClientApproved, // 2
  OAuthClientRejected, // 3
  OAuthClientSuspended, // 4
  OAuthClientAutoApproved, // 5
  OAuthGrantIssued, // 6
  OAuthGrantRefreshed, // 7
  OAuthGrantRevoked, // 8
  OAuthScopeDenied, // 9
  OAuthRefreshReplayDetected, // 10
  RateLimitBreached, // 11
  JwksKeyRotated, // 12
  JwksKeyRetired, // 13
  JwksKeyExpired, // 14
  KeyCompromiseRotation, // 15
  WorkspaceRegistryProfileChanged, // 16
  WorkspaceBrandingUpdated, // 17
]);

export type GatewayAuditEvent = z.infer<typeof GatewayAuditEvent>;
export type GatewayAuditEventType = GatewayAuditEvent['type'];

/**
 * Helper: narrow on the 'type' discriminator. Useful in 7.5's audit-log
 * resource consumer where renderers fan out by `type`.
 */
export type GatewayAuditEventByType<T extends GatewayAuditEventType> = Extract<
  GatewayAuditEvent,
  { type: T }
>;
